import React, { useEffect, useRef, useState } from "react";
import { usePageTitle } from "../lib/brand.js";
import ClientBreadcrumb from "../components/ClientBreadcrumb.jsx";
import PreviewBanner from "../components/PreviewBanner.jsx";
import { useIsPreviewTab } from "../hooks/useIsPreviewTab.js";
import { useParams, Link } from "react-router-dom";
import { normalizeMediaUrl } from "../lib/mediaUrl.js";
import { useAuth } from "../hooks/useAuth.jsx";
import { useApi } from "../hooks/useApi.js";
import { useIsCompact } from "../hooks/useIsCompact.js";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";
import Composer from "../components/Composer.jsx";
import MyNotes from "../components/MyNotes.jsx";
import AboutSheet from "../components/AboutSheet.jsx";
import { aboutSeen, markAboutSeen } from "../lib/aboutSheet.js";
import { readPoster, writePoster, pickTab, minuteIsNew, localDay } from "../lib/rotating.js";
import ShiftLog from "../components/ShiftLog.jsx";
import PlayThroughPlayer from "./PlayThroughPlayer.jsx";
import { useSectionPlaythroughs } from "../hooks/useSectionPlaythroughs.js";
import { sectionLabel, playthroughBase } from "../lib/sections.js";

/**
 * MeetPage — the DSP's front door, with a return path (redesigned Nov 1).
 *
 * One poster, always at the top and always the same size. It holds one of
 * two films, and the picture is the play button:
 *
 *   Introduction — "Meet Elena". Until she has watched it through, it is the
 *   only film and nothing asks her anything.
 *   Today's minute — one scene, picked by the day. Once she has finished the
 *   introduction, two quiet tabs sit inside the poster's top corner,
 *   "Introduction | Today's minute", with a small dot when today's is new.
 *   From the day after she finishes, Today's minute leads on its own; a tab
 *   she taps holds for the rest of that day (lib/rotating.js).
 *
 * No question and no pop-up: the poster changes with her, and the way back
 * is always in the same place. Switching crossfades. Nothing is drawn in the
 * poster's place until the play-through, the view and the scenes have
 * loaded, so the introduction never flashes before Today's minute.
 *
 * Under the poster, the one line the profile is built around. Then, before
 * the introduction is done: her story in parts first, the rest below it.
 * After: what the last shifts learned, the bar, her notes, her story.
 *
 * One bar (Composer): Ask | Note for the team (CAP_TEAM), Ask alone for
 * anyone else. Her notes are one card with "Last time you said…" folded in.
 * What the captions used to say is said once, in the About sheet — opened
 * the first time she puts the cursor in the bar, and any time from the ⓘ
 * beside the bar's Ask | Note switch — help where the question arises, not
 * a row of its own at the top (Nov 2).
 * With no live sections, there is no list heading and nothing under it.
 *
 * A profile with no finalized playthrough must never look broken — the
 * poster becomes the hero photo with the name, and the list has no plays.
 */

const SECTION_ORDER = [
  "meet", "communicate", "sensory", "passions", "day",
  "working_on", "hard_times", "health", "people", "community",
];
const TYPE_TO_SLUG = {
  meet: "meet", communicate: "communicate", sensory: "sensory", passions: "passions",
  day: "day", working_on: "working-on", hard_times: "hard-times", health: "health",
  people: "people", community: "community",
};
// Rows shown on a phone before "and N more" — enough to see the shape of the
// story with Ask still on screen.
const COMPACT_ROWS = 5;

export function pronounsOf(profile) {
  const p = (profile?.pronouns || "").toLowerCase();
  if (p.startsWith("he"))   return { subj: "he",   poss: "his" };
  if (p.startsWith("she"))  return { subj: "she",  poss: "her" };
  // Unknown or unset pronouns read "they/their" (Oct 25) — was "she/her".
  return { subj: "they", poss: "their" };
}

// Section names with the person's pronouns (Oct 25) — one source for every
// page, lib/sections.js. The old regex here turned "How she communicates"
// into "How they communicates".
function labelFor(type, poss) {
  return sectionLabel(type, poss === "his" ? "he" : poss === "her" ? "she" : "they", { sentence: true });
}

export function mmss(ms) {
  if (!ms || !isFinite(ms)) return null;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}


function PlayGlyph({ size = 18, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" style={{ display: "block", marginLeft: 2 }}>
      <path d="M4 2.5 L13 8 L4 13.5 Z" fill={color} />
    </svg>
  );
}

export default function MeetPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const compact = useIsCompact();
  const previewTab = useIsPreviewTab();
  const isCoordinator = user?.role === "coordinator" || user?.role === "agency_admin";
  const [expanded, setExpanded] = useState(false);
  // What is playing in the poster's frame: null, or { sceneId, sectionType }.
  // Set by the poster and the row plays; cleared by the player's end card or
  // its ×. On clear, the view and echo refetch so the page moves on without
  // a reload (C1).
  const [playing, setPlaying] = useState(null);
  // Which film the poster holds — the introduction or Today's minute
  // (lib/rotating.js): the rule, or the tab she tapped today.
  const [stored, setStored] = useState(() => readPoster(clientId));
  const today = localDay();
  function choose(tab) { setStored(writePoster(clientId, { tab, tabDay: today })); }
  // Once the poster has been decided, refetches (after the player closes)
  // don't blank it again.
  const posterSettled = useRef(false);

  const { data: profile, loading, error, refetch } = useApi(`/clients/${clientId}/profile`);
  const { data: pt, loading: ptLoading } = useApi(`/clients/${clientId}/playthrough`);
  // Watchable when a finalized output is stored — also while a new draft is
  // being authored or reviewed (Oct 28: authoring no longer takes it down).
  const playthroughReady = !!pt?.cg_output_available;
  const { data: scenesData, loading: scenesLoading } = useApi(playthroughReady ? `/clients/${clientId}/playthrough/scenes` : null);
  const { data: view, loading: viewLoading, refetch: refetchView } = useApi(playthroughReady ? `/clients/${clientId}/playthrough/view` : null);
  // The day she finished the introduction: Today's minute leads from the
  // next day on. First seen here, it's the day of her last watch (so someone
  // who finished it last week gets Today's minute now, on any device).
  const viewDone = !!view?.completed;
  const lastWatched = view?.last_watched_at || null;
  useEffect(() => {
    if (!viewDone || readPoster(clientId).doneDay) return;
    const d = lastWatched ? new Date(lastWatched) : null;
    setStored(writePoster(clientId, { doneDay: d && !Number.isNaN(d.getTime()) ? localDay(d) : localDay() }));
  }, [viewDone, lastWatched, clientId]);
  const { data: mine, refetch: refetchMine } = useApi(user ? `/clients/${clientId}/reflections/mine?limit=1` : null);
  // Sections with their own finalized play-through (Oct 20). Empty while the
  // feature is off: the profile then carries no section_playthroughs.
  const sectionPts = useSectionPlaythroughs(clientId, profile?.section_playthroughs, profile?.section_live);
  // On the team around this person: may write notes and has notes of her own.
  const team = !!user && (profile?.viewer_capabilities || []).includes("team");
  const [about, setAbout] = useState(false);
  function firstUse() {
    if (!aboutSeen()) setAbout(true);
  }
  function closeAbout() {
    markAboutSeen();
    setAbout(false);
  }
  const { data: myNotes, refetch: refetchNotes } = useApi(team ? `/clients/${clientId}/notes/mine` : null);
  const shiftNotesOn = team && !!profile?.shift_sharing && profile.shift_sharing !== "off";
  const { data: shiftLog } = useApi(shiftNotesOn ? `/clients/${clientId}/notes/shift-log` : null);

  function stopPlaying() {
    setPlaying(null);
    refetchView?.(); refetchMine?.();
  }
  // Every play goes through here. The stage is fixed over the page (Plan G),
  // so the page keeps its scroll position for when the stage closes.
  function startPlaying(sceneId, sectionType = null) {
    setPlaying({ sceneId, sectionType });
  }
  usePageTitle(profile?.display_name);

  if (loading) return <LoadingSpinner message="Loading profile…" />;
  if (error)   return <ErrorBanner message={error} onRetry={refetch} />;
  if (!profile) return null;

  const name = profile.display_name;
  const { subj, poss } = pronounsOf(profile);
  const Poss = poss === "her" ? "Her" : poss === "his" ? "His" : "Their";
  const sections = [...(profile.sections || [])]
    .filter(s => s.status === "live" && s.has_content !== false)
    .sort((a, b) => SECTION_ORDER.indexOf(a.section_type) - SECTION_ORDER.indexOf(b.section_type));
  const meetLead = sections.find(s => s.section_type === "meet")?.lead_paragraph;
  const firstSentence = meetLead ? (meetLead.match(/^[^.!?]+[.!?]/)?.[0] || meetLead) : null;

  const scenes = scenesData?.scenes || [];
  const minute = scenesData?.today || null;
  const sceneFor = type => scenes.find(s => s.section_type === type) || null;
  const fullMs = scenes.reduce((a, s) => a + (s.duration_ms || 0), 0);

  const introDone = !!view?.completed;
  const tab = pickTab({ introDone, hasMinute: !!minute, stored, today });
  const showTabs = introDone && !!minute && playthroughReady;
  const minuteNew = showTabs && minuteIsNew({ tab, stored, today });
  const lastSaid  = mine?.[0] || null;
  const heroUrl   = profile.hero_photo_media_id ? `/api/clients/${clientId}/media/${profile.hero_photo_media_id}` : null;
  // Don't draw a poster until we know which one: the introduction used to
  // flash for half a second before Today's minute replaced it (Nov 1).
  const posterPending = !posterSettled.current &&
    (ptLoading || (playthroughReady && (viewLoading || scenesLoading)));
  if (!posterPending) posterSettled.current = true;

  const minuteBundle = minute?.bundle_section || null;
  const minuteFrom = minuteBundle ? `from “${labelFor(minuteBundle, poss)}”` : `from ${poss} introduction`;

  // One poster, one place, either film (Nov 1, rev 2).
  let poster;
  if (playthroughReady && tab === "minute") {
    poster = {
      key: "minute",
      img: normalizeMediaUrl(minute.poster_url) || heroUrl,
      focus: minute.poster_focus || null,
      title: minute.title || labelFor(minute.section_type, poss),
      sub: [mmss(minute.duration_ms), minuteFrom].filter(Boolean).join(" · "),
      playLabel: `Play: Today's minute, ${minute.title || ""}`,
      onPlay: () => {
        setStored(writePoster(clientId, { minuteDay: today }));
        startPlaying(minute.scene_id, minuteBundle);
      },
    };
  } else if (playthroughReady) {
    poster = {
      key: "intro",
      img: heroUrl,
      title: `Meet ${name}`,
      sub: [`${Poss} introduction`, mmss(fullMs)].filter(Boolean).join(" · "),
      playLabel: `Play: Meet ${name}`,
      onPlay: () => startPlaying(null),
    };
  } else {
    poster = { key: "none", img: heroUrl, title: name, sub: profile.hero_oneliner || profile.location_display || "", onPlay: null };
  }
  const tabs = showTabs ? { tab, onChoose: choose, minuteNew } : null;

  const visibleRows = compact && !expanded ? sections.slice(0, COMPACT_ROWS) : sections;
  const hiddenCount = sections.length - visibleRows.length;

  const player = playing?.sectionType ? (
    // A section's own play-through: several short scenes about that part — or
    // one of them, when it is Today's minute. The base is the section's own,
    // whether or not its scene index has loaded yet.
    <PlayThroughPlayer key={`section-${playing.sectionType}-${playing.sceneId || "full"}`} embedded autoplay clientId={clientId}
                       playthroughBase={sectionPts[playing.sectionType]?.base || playthroughBase(clientId, playing.sectionType)}
                       sceneId={playing.sceneId}
                       counterLabel={labelFor(playing.sectionType, poss)}
                       onExit={stopPlaying} />
  ) : playing ? (
    <PlayThroughPlayer key={playing.sceneId || "full"} embedded autoplay clientId={clientId}
                       sceneId={playing.sceneId} onExit={stopPlaying}
                       offerImpressions={!!minute && playing.sceneId === minute.scene_id} />
  ) : null;

  const shiftLogCard = shiftNotesOn ? <ShiftLog clientId={clientId} poss={poss} data={shiftLog} /> : null;

  const composer = (
    <Composer clientId={clientId} clientName={name} poss={poss} team={team}
              shiftSharing={profile.shift_sharing} onFirstUse={firstUse}
              onInfo={() => setAbout(true)}
              onNoteSaved={() => refetchNotes?.()} />
  );

  // Her notes, with "Last time you said" folded in. Someone signed in but not
  // on the team keeps the echo on its own, quietly.
  const notesCard = team ? (
    <MyNotes clientId={clientId} clientName={name} poss={poss} data={myNotes} onChanged={refetchNotes} lastSaid={lastSaid} />
  ) : (introDone && lastSaid) ? (
    <p style={styles.echoAlone}>Last time you said: “{lastSaid.text}”</p>
  ) : null;

  const sectionList = sections.length > 0 && (
    <section aria-label="Sections">
      <h2 style={styles.listHeading}>{Poss} story, in parts</h2>
      <div style={styles.list}>
        {visibleRows.map(sec => {
          // A section's own play-through, when finalized, takes the row's play;
          // otherwise the row plays its scene from the introduction.
          const spt    = sectionPts[sec.section_type] || null;
          const scene  = spt ? null : sceneFor(sec.section_type);
          const played = !!(scene && view?.completed);
          const label  = labelFor(sec.section_type, poss);
          const page   = `/clients/${clientId}/${TYPE_TO_SLUG[sec.section_type]}`;
          return (
            <div key={sec.id} style={styles.row}>
              <Link to={page} style={styles.rowLink} aria-label={`${label} — more`}>
                <span style={styles.rowName}>{label}</span>
                <span style={styles.rowMore}>More ›</span>
              </Link>
              <span style={styles.rowPillCol}>
                {spt && (
                  <button type="button" onClick={() => startPlaying(null, sec.section_type)}
                          aria-label={`Play ${label}, ${mmss(spt.durationMs)}`}
                          style={styles.rowPill}>
                    <PlayGlyph size={10} color="var(--color-text-primary)" />
                    <span style={styles.rowDur}>{mmss(spt.durationMs)}</span>
                  </button>
                )}
                {scene && (
                  <button type="button" onClick={() => startPlaying(scene.scene_id)}
                          aria-label={`Play ${label}, ${mmss(scene.duration_ms)}`}
                          style={{ ...styles.rowPill, ...(played ? styles.rowPillOn : {}) }}>
                    <PlayGlyph size={10} color={played ? "var(--color-accent)" : "var(--color-text-primary)"} />
                    <span style={styles.rowDur}>{mmss(scene.duration_ms)}</span>
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <div style={styles.listFoot}>
          <button type="button" style={styles.more} onClick={() => setExpanded(true)}>and {hiddenCount} more ↓</button>
        </div>
      )}
    </section>
  );

  return (
    <div style={styles.page}>
      {previewTab ? (
        <PreviewBanner label="how this looks to a DSP" />
      ) : (
        <div style={{ ...styles.breadcrumbBar, ...(compact ? styles.breadcrumbBarCompact : {}) }}>
          <ClientBreadcrumb clientName={name} role={user?.role} />
          {isCoordinator && (
            <Link to={`/coordinator/clients/${clientId}/edit`} style={styles.coordLink}>Coordinator →</Link>
          )}
        </div>
      )}
      <div style={{ ...styles.content, ...(compact ? styles.contentCompact : {}) }}>

        <AboutSheet open={about} onClose={closeAbout} name={name} team={team} signedIn={!!user} />

        {/* The poster — always here, always this size. It holds the
            introduction or Today's minute; the player takes its place. */}
        {player || (posterPending
          ? <div style={{ ...styles.poster, height: compact ? 300 : 340, background: "var(--color-bg-inset)" }} aria-busy="true" />
          : <Poster poster={poster} name={name} compact={compact} tabs={tabs} />)}
        {!playing && firstSentence && <p style={styles.lead}>{firstSentence}</p>}

        {introDone ? (
          // After the introduction: what the last shifts learned, the bar,
          // her notes, then her story in parts.
          <>{shiftLogCard}{composer}{notesCard}{sectionList}</>
        ) : (
          // Until then the introduction is a new hire's first thing; the rest
          // waits below her story.
          <>{sectionList}{shiftLogCard}{composer}{notesCard}</>
        )}
      </div>
    </div>
  );
}

/**
 * Where a cropped poster is anchored: the scene's face point when PE found
 * one (the same target the player pans toward); otherwise the centre, with
 * no guess about where the subject is (David, Oct 16).
 */
export function posterPosition(focus) {
  if (focus && Number.isFinite(focus.x) && Number.isFinite(focus.y)) {
    return `${Math.round(focus.x * 100)}% ${Math.round(focus.y * 100)}%`;
  }
  return "50% 50%";
}

function Poster({ poster, name, compact, tabs = null }) {
  const h = compact ? 300 : 340;
  const onImg = !!poster.img || !!tabs;
  return (
    <div style={{ ...styles.poster, height: h, background: poster.img ? "#6E5C49" : "var(--color-accent-subtle)" }}
         aria-label={poster.onPlay ? undefined : name}>
      <style>{POSTER_CSS}</style>
      {/* Keyed by film, so switching crossfades instead of snapping. */}
      <div key={poster.key} className="cg-poster-fade" style={styles.posterLayer}>
        {poster.img && <img src={poster.img} alt="" style={{ ...styles.posterImg, objectPosition: posterPosition(poster.focus) }} />}
        <div style={styles.posterScrim} />
        {tabs && <div style={styles.posterTopScrim} />}
        <div style={styles.posterText} aria-hidden={poster.onPlay ? "true" : undefined}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...styles.posterTitle, color: onImg ? "#fff" : "var(--color-text-primary)" }}>{poster.title}</div>
            {poster.sub && <div style={{ ...styles.posterSub, color: onImg ? "rgba(255,255,255,0.88)" : "var(--color-text-secondary)" }}>{poster.sub}</div>}
          </div>
          {poster.onPlay && <span style={styles.posterPlay}><PlayGlyph size={22} /></span>}
        </div>
      </div>
      {/* The whole picture is the play button. */}
      {poster.onPlay && (
        <button type="button" onClick={poster.onPlay} aria-label={poster.playLabel} className="cg-poster-hit" style={styles.posterHit} />
      )}
      {/* After the introduction: which film the poster holds. */}
      {tabs && (
        <div role="tablist" aria-label="What to watch" style={styles.tabs}>
          {[["intro", "Introduction"], ["minute", "Today's minute"]].map(([k, label]) => {
            const on = tabs.tab === k;
            return (
              <button key={k} type="button" role="tab" aria-selected={on} onClick={() => tabs.onChoose(k)}
                      style={{ ...styles.tab, ...(on ? styles.tabOn : {}) }}>
                {label}
                {k === "minute" && tabs.minuteNew && <span style={styles.tabDot} aria-label="new" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const POSTER_CSS = `
@keyframes cgPosterFade { from { opacity: 0; transform: scale(1.015); } to { opacity: 1; transform: none; } }
.cg-poster-fade { animation: cgPosterFade 280ms ease-out; }
.cg-poster-hit:focus-visible { outline: 3px solid var(--color-accent); outline-offset: -3px; }
@media (prefers-reduced-motion: reduce) { .cg-poster-fade { animation: none; } }
`;

const styles = {
  page:          { minHeight: "100dvh", background: "var(--color-bg-base)", paddingBottom: "var(--space-6)" },
  breadcrumbBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "var(--space-3) var(--space-6)", borderBottom: "1px solid var(--color-border)", background: "var(--color-surface)" },
  breadcrumbBarCompact: { padding: "var(--space-3) var(--space-4)" },
  content:       { maxWidth: 720, margin: "0 auto", padding: "var(--space-5) var(--space-6) 0", display: "flex", flexDirection: "column", gap: "var(--space-5)" },
  contentCompact:{ padding: "var(--space-4) var(--space-4) 0", gap: "var(--space-4)" },
  coordLink:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", textDecoration: "none", minHeight: "var(--tap-target-min)", display: "inline-flex", alignItems: "center" },

  poster:        { position: "relative", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "0 1px 2px rgba(26,25,21,0.08), 0 8px 24px rgba(26,25,21,0.10)" },
  posterLayer:   { position: "absolute", inset: 0 },
  posterHit:     { position: "absolute", inset: 0, width: "100%", height: "100%", padding: 0, border: 0, background: "transparent", cursor: "pointer", borderRadius: "inherit" },
  posterTopScrim:{ position: "absolute", left: 0, right: 0, top: 0, height: 72, background: "linear-gradient(rgba(26,25,21,0.35), transparent)" },
  tabs:          { position: "absolute", top: 12, left: 12, display: "inline-flex", padding: 3, borderRadius: 999, background: "rgba(26,25,21,0.42)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", zIndex: 2 },
  tab:           { position: "relative", font: "inherit", fontSize: 13, fontWeight: 500, height: 32, padding: "0 14px", border: 0, borderRadius: 999, background: "transparent", color: "rgba(255,255,255,0.92)", cursor: "pointer", whiteSpace: "nowrap", transition: "background 160ms, color 160ms" },
  tabOn:         { background: "#fff", color: "#1a1915", fontWeight: 600, boxShadow: "0 1px 2px rgba(0,0,0,0.18)" },
  tabDot:        { position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: 999, background: "#8fdcb8", boxShadow: "0 0 0 2px rgba(26,25,21,0.42)" },
  posterImg:     { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" },
  posterScrim:   { position: "absolute", left: 0, right: 0, bottom: 0, height: "55%", background: "linear-gradient(transparent, rgba(26,25,21,0.75))" },
  posterText:    { position: "absolute", left: 20, right: 16, bottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  posterTitle:   { fontFamily: "var(--font-serif)", fontSize: 26, fontWeight: 500, lineHeight: 1.18, textWrap: "balance" },
  posterSub:     { fontSize: "var(--text-sm)", marginTop: 4 },
  posterPlay:    { width: 54, height: 54, borderRadius: 999, background: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  echoAlone:     { margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  lead:          { fontFamily: "var(--font-serif)", fontSize: "var(--text-lg)", lineHeight: 1.5, color: "var(--color-text-primary)", margin: 0 },

  listHeading:   { fontSize: "var(--text-xs)", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-tertiary)", fontWeight: "var(--weight-semibold)", margin: "0 0 4px" },
  list:          { display: "flex", flexDirection: "column" },
  row:           { display: "flex", alignItems: "center", gap: 12, minHeight: 52, borderTop: "1px solid var(--color-border)" },
  rowLink:       { flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, minHeight: 52, textDecoration: "none", color: "inherit" },
  rowMore:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", whiteSpace: "nowrap" },
  rowPillCol:    { width: 78, display: "flex", justifyContent: "flex-end", flexShrink: 0, borderLeft: "1px solid var(--color-border)", minHeight: 32, alignItems: "center", paddingLeft: 12 },
  rowPill:       { font: "inherit", display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 10px 0 9px", borderRadius: 999, border: "1.5px solid var(--color-border-strong)", background: "var(--color-surface)", color: "var(--color-text-primary)", cursor: "pointer" },
  rowPillOn:     { border: "1.5px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-accent)" },
  rowName:       { flex: 1, fontSize: "var(--text-base)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowDur:        { fontSize: "var(--text-xs)", fontVariantNumeric: "tabular-nums", fontWeight: "var(--weight-medium)" },
  listFoot:      { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, paddingTop: 8, flexWrap: "wrap" },
  more:          { font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", padding: 0, cursor: "pointer" },
};
